import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Button, 
    Typography,
    useMediaQuery,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    ListItemIcon,
} from '@mui/material';
import { Add, Feed, MoreVert } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { InitiateReviewModal } from './modals/InitiateReviewModal';
import { StringKeys } from '../../../resources/strings/StringKeys';
import { useOrganizationContext } from '../../../context/organization_context/OrganizationContext';
import AppSidebar from '../../../components/navbar/DesktopAppSidebar';
import PerformanceReviewsTable from './PerformanceReviewsTable';
import { useUserContext } from '../../../context/user_context/UserContext';
import { performanceReviewSnapshotListener, performanceReviewTemplatesSnapshotListener } from '../../../repository/PerformanceReviewDAO';
import { Loading } from '../../../components/loading/Loading';
import usePermissions from '../../../utils/permissions/UsePermissions';
import { Modules } from '../../../utils/permissions/Modules';
import { PermissionTypes } from '../../../utils/permissions/PermissionTypes';
import { handleMenuClick, handleMenuClose } from '../../../components/utils/MenuHandlers';

const PerformanceReviews = () => {
    const { t } = useTranslation();
    const { firebaseUser } = useUserContext();
    const { hasPermission } = usePermissions();
    const { organizationEmployees, isUserDepartmentManager } = useOrganizationContext();
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width:600px)');

    const [anchorEl, setAnchorEl] = useState(null);
    const [reviews, setReviews] = useState();
    const [templates, setTemplates] = useState();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [filteredReviews, setFilteredReviews] = useState([]);
    const [managedEmployees, setManagedEmployees] = useState([]);

    useEffect(() => {
        if (firebaseUser) {
            const unsubscribe = performanceReviewTemplatesSnapshotListener(firebaseUser.organization, setTemplates);
            return () => unsubscribe();
        }
    }, [firebaseUser]);

    useEffect(() => {
        if (firebaseUser) {
            const unsubscribe = performanceReviewSnapshotListener(firebaseUser.organization, setReviews);
            return () => unsubscribe();
        }
    }, [firebaseUser]);

    useEffect(() => {
        if (reviews && firebaseUser && organizationEmployees) {
            // Check permissions
            const canReadAllReviews = hasPermission(Modules.PERFORMANCE_REVIEWS, PermissionTypes.READ_ALL_REVIEWS) ||
                                    hasPermission(Modules.PERFORMANCE_REVIEWS, PermissionTypes.READ_AND_WRITE_ALL_REVIEWS);
            const canReadManagedReviews = hasPermission(Modules.PERFORMANCE_REVIEWS, PermissionTypes.READ_AND_WRITE_REVIEWS_FOR_MANAGED_EMPLOYEES);
        
            if (canReadAllReviews) {
                // Users with read all permissions see all reviews
                setFilteredReviews(reviews);
                setManagedEmployees(organizationEmployees.filter(emp => emp.id !== firebaseUser.uid));
            } else if (canReadManagedReviews && isUserDepartmentManager(firebaseUser.uid)) {
                // For department managers, only show reviews from their department
                const managerEmployee = organizationEmployees.find(emp => emp.id === firebaseUser.uid);
                const departmentId = managerEmployee?.department_id;
                
                const departmentEmployees = organizationEmployees.filter(emp => 
                    emp.department_id === departmentId && emp.id !== firebaseUser.uid
                );
                
                const departmentReviews = reviews.filter(review => 
                    departmentEmployees.some(emp => emp.id === review.employee_id)
                );
                
                setManagedEmployees(departmentEmployees);
                setFilteredReviews(departmentReviews);
            } else {
                // No permission to view reviews
                setManagedEmployees([]);
                setFilteredReviews([]);
            }
        }
    }, [reviews, firebaseUser, organizationEmployees, isUserDepartmentManager]);

    if(!templates || !reviews) return <AppSidebar content={<Loading />} />

    const canInitiateReviews = 
        hasPermission(Modules.PERFORMANCE_REVIEWS, PermissionTypes.READ_AND_WRITE_ALL_REVIEWS) ||
        (hasPermission(Modules.PERFORMANCE_REVIEWS, PermissionTypes.READ_AND_WRITE_REVIEWS_FOR_MANAGED_EMPLOYEES) && 
        isUserDepartmentManager(firebaseUser?.uid));
    const canModifyReviewTemplates = hasPermission(Modules.PERFORMANCE_REVIEWS, PermissionTypes.MODIFY_REVIEW_TEMPLATES);

    return (
        <AppSidebar
            content={
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12}>
                        <Grid container spacing={2} alignItems="center" justifyContent="space-between">
                            {!isMobile && (
                                <>
                                    <Grid item>
                                        <Typography variant="h5">{t(StringKeys.PERFORMANCE_REVIEWS)}</Typography>
                                    </Grid>
                                    <Grid item>
                                        <Grid container spacing={2} alignItems="center">
                                            {canModifyReviewTemplates && (
                                                <>
                                                    <Grid item>
                                                        <Button
                                                            variant="contained"
                                                            startIcon={<Feed />}
                                                            onClick={() => navigate('/performance-review-templates')}
                                                        >
                                                            {t(StringKeys.TEMPLATES)}
                                                        </Button>
                                                    </Grid>
                                                </>
                                            )}
                                            {
                                                canInitiateReviews && (
                                                    <>
                                                        <Grid item>
                                                            <Button
                                                                variant="contained"
                                                                startIcon={<Add />}
                                                                onClick={() => setIsModalOpen(true)}
                                                            >
                                                                {t(StringKeys.INITIATE_REVIEW)}
                                                            </Button>
                                                        </Grid>
                                                    </>
                                                )
                                            }
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                            {isMobile && (
                                <>
                                    <Grid item>
                                        <Typography variant="h6">{t(StringKeys.PERFORMANCE_REVIEWS)}</Typography>
                                    </Grid>
                                    {(canModifyReviewTemplates || canInitiateReviews) && (
                                        <Grid item style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                            <IconButton onClick={(event) => handleMenuClick(event, setAnchorEl)}>
                                                <MoreVert />
                                            </IconButton>
                                            <Menu
                                                anchorEl={anchorEl}
                                                open={Boolean(anchorEl)}
                                                onClose={() => handleMenuClose(setAnchorEl)}
                                            >
                                                {canModifyReviewTemplates && (
                                                    <MenuItem onClick={() => { 
                                                        navigate('/performance-review-templates');
                                                        handleMenuClose(setAnchorEl);
                                                    }}>
                                                        <ListItemIcon>
                                                            <Feed />
                                                        </ListItemIcon>
                                                        {t(StringKeys.TEMPLATES)}
                                                    </MenuItem>
                                                )}
                                                {canInitiateReviews && (
                                                    <MenuItem onClick={() => { 
                                                        setIsModalOpen(true);
                                                        handleMenuClose(setAnchorEl);
                                                    }}>
                                                        <ListItemIcon>
                                                            <Add />
                                                        </ListItemIcon>
                                                        {t(StringKeys.INITIATE_REVIEW)}
                                                    </MenuItem>
                                                )}
                                            </Menu>
                                        </Grid>
                                    )}
                                </>
                            )}
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <PerformanceReviewsTable 
                            reviews={filteredReviews}
                            onEditReview={(reviewId) => navigate(`/performance-reviews/${reviewId}`)}
                        />
                    </Grid>

                    <InitiateReviewModal
                        open={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        employees={managedEmployees.length > 0 ? managedEmployees : organizationEmployees}
                        templates={templates}
                    />
                </Grid>
            }
        />
    );
};

export default PerformanceReviews;
